import {logLcs, sendAULog} from '@financial/common-logs'
import {Middleware} from '@reduxjs/toolkit'

import {isArray} from '@shared/helpers/lang'

import {NTM} from '@web/helpers/log'
import {auLogPageFieldSeletor, CommonState} from '@web/slices/common/slice'

import {pageViewAction} from './actions'
import {AUTargetMap} from './auTargetMap'
import {PageViewEventType, PageViewLoggers, PageViewValueType} from './constants'
import uiEventAction, {UiEventType} from './uiEventAction'

export type CreaterProps<AppState> = {
    nClicksTargetMap?: Map<UiEventType, string> | Map<UiEventType, [string, string]>
    convertNClicksCode?: (args: {uiEvent: UiEventType; value: string | [string, string]; state: AppState}) => string
    nClicksExtraInfoCreator?: (uiEvent: UiEventType, state: AppState) => Maybe<Record<string, string>>
    /** lcs / au */
    pageViewTargetMap?: Map<PageViewEventType, PageViewLoggers>
    pageViewLogger?: {
        getLcs?: (args: {
            target: PageViewEventType
            value?: PageViewValueType
            code: string
            state: AppState
        }) => Parameters<typeof logLcs>[0] | {[key: string]: string}
    }
}

/**
 * `UI 이벤트 액션을 추적하여 증적 기록을 요청하는 미들웨어` 를 생성합니다.
 */
const uiEventLoggerCreator =
    <AppState extends {common: CommonState}>({
        nClicksTargetMap,
        convertNClicksCode,
        nClicksExtraInfoCreator,
        pageViewTargetMap,
        pageViewLogger,
    }: CreaterProps<AppState>): Middleware<object, AppState> =>
    ({getState}) =>
    (next) =>
    (action) => {
        const state = getState()
        // NTM 모듈이 활성화되지 않은 상태에서만 nclicks 실행 처리
        if (uiEventAction.match(action)) {
            const {uiEvent, event} = action.payload
            if (nClicksTargetMap?.has(uiEvent)) {
                const value = nClicksTargetMap.get(uiEvent)
                if (value) {
                    // convertNClicksCode 가 없는 경우 codes[0]를 사용한다.
                    const code = isArray(value) ? value[0] : value
                    const convertedNClicksCode =
                        convertNClicksCode?.({
                            state,
                            uiEvent,
                            value,
                        }) ?? code

                    const extra = nClicksExtraInfoCreator?.(uiEvent, getState())

                    NTM.isActivated()
                        ? NTM.triggerClick({
                              code: convertedNClicksCode.split('.')[1] ?? convertedNClicksCode,
                              ...(extra ? {extra} : null),
                          })
                        : window.nclk_v2_with_obj({
                              e: event,
                              a: convertedNClicksCode,
                              g: extra?.['extraData'],
                              // 이전에 g_query컬럼에 축적 된 데이터가 있어 하위호환용

                              g_query: extra?.['extraData'],
                          })
                }
            }
            if (AUTargetMap.has(uiEvent)) {
                const targetValue = AUTargetMap.get(uiEvent)
                if (targetValue) {
                    const page = auLogPageFieldSeletor(state)

                    const {
                        actionType,
                        value: [service, area, code],
                    } = targetValue
                    sendAULog({service, area, code, page}, actionType)
                }
            }
        }

        if (pageViewAction.match(action)) {
            if (!NTM.isActivated()) {
                const {target, value} = action.payload
                if (pageViewLogger && pageViewTargetMap?.has(target)) {
                    const loggers = pageViewTargetMap.get(target)
                    loggers?.forEach((logger) => {
                        const lcsData = pageViewLogger?.getLcs?.({
                            target,
                            code: logger,
                            value,
                            state: getState(),
                        })
                        /**
                         * - lcs @deprecated 예정
                         * - SPA 기반이므로 sti 는 계속 유지 (신규페이지도 지정 가능)
                         * - 향후 전체 앱에 대해 nlog 사용 전환
                         * - @TODO: logLcs 로직 제거
                         */
                        lcsData && logLcs(lcsData as Parameters<typeof logLcs>[0])
                    })
                }
            }
        }

        return next(action)
    }

export default uiEventLoggerCreator
